import { useEffect, useMemo, useState } from "react";
import useCustomerList from "src/hooks/user/customer/useCustomerList";
import { v4 as uuid } from "uuid";
import { customerFilter } from "./constant";
import { Box, Checkbox, Typography } from "@mui/material";
import convertCurrency, { convertIntMoney } from "src/helper/convertCurrency";
import { USER_TYPE, USER_TYPE_TRANSLATE } from "src/constant";
import useQueryParams from "src/hooks/useQueryParams";

export default function useCustomerBody({ debouncedKey, searchBy, debouncedSale }) {
  const [option] = useQueryParams();
  const [selectedIds, setSelectedIds] = useState([]);

  const { data: customers, mutate } = useCustomerList({
    limit: 50,
    skip: 0,
    // searchValue: !!keyword ? keyword : undefined,
    searchField: !!searchBy ? (searchBy === "saleAndClient" ? "fullName" : customerFilter[searchBy - 1]?.value) : customerFilter[0]?.value,
    sales: !!debouncedSale ? debouncedSale : undefined,
    type: USER_TYPE[option?.tab],
    q: !!debouncedKey ? debouncedKey : undefined,
    sortField: "sortName",
    sortOrder: "asc",
  });

  const pageIds = useMemo(() => (Array.isArray(customers?.data) ? customers.data.map((v) => v?._id).filter(Boolean) : []), [customers?.data]);

  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const somePageSelected = pageIds.some((id) => selectedIds.includes(id)) && !allPageSelected;

  const handleToggleAll = () => {
    setSelectedIds((prev) => {
      if (pageIds.length === 0) return prev;
      if (pageIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !pageIds.includes(id));
      }
      return [...new Set([...prev, ...pageIds])];
    });
  };

  const handleToggleOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Chỉ reset khi đổi tab loại user; giữ nguyên lựa chọn khi tìm kiếm / lọc sale (đồng bộ qua effect bên dưới).
  useEffect(() => {
    setSelectedIds([]);
  }, [option?.tab]);

  useEffect(() => {
    if (!Array.isArray(customers?.data)) return;
    const existingIds = new Set(customers.data.map((v) => v?._id).filter(Boolean));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => existingIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [customers?.data]);

  const header = [
    {
      id: uuid(),
      comp: (
        <Checkbox
          checked={allPageSelected}
          indeterminate={somePageSelected}
          onChange={handleToggleAll}
          inputProps={{ "aria-label": "Chọn tất cả khách hàng trên danh sách" }}
        />
      ),
      align: "center",
    },
    {
      id: uuid(),
      title: "STT",
      width: "20px",
      align: "center",
    },
    {
      id: uuid(),
      title: "Khách hàng",
      align: "center",
    },
    {
      id: uuid(),
      title: "SĐT - Email",
      align: "center",
    },
    {
      id: uuid(),
      title: "NV sale phụ trách",
      align: "center",
    },
    {
      id: uuid(),
      title: "Hạn mức tổng công nợ",
      align: "center",
    },
    {
      id: uuid(),
      title: "Công nợ hiện tại",
      align: "center",
    },
    {
      id: uuid(),
      title: "Thao tác",
      align: "center",
    },
  ];

  const bodyList = Array.isArray(customers?.data)
    ? customers?.data?.map((v, index) => ({
        ...v,
        checkbox: (
          <Checkbox
            checked={selectedIds.includes(v._id)}
            onChange={() => handleToggleOne(v._id)}
            inputProps={{ "aria-label": `Chọn ${v?.fullName ?? "khách hàng"}` }}
          />
        ),
        no: <Typography align="center">{index + 1}</Typography>,
        name: (
          <Box textAlign="center">
            <Typography>{v?.fullName ?? "-"}</Typography>
            <Typography variant="body2">({USER_TYPE_TRANSLATE[v?.type]})</Typography>
          </Box>
        ),
        info: (
          <Box>
            <Typography sx={{ fontSize: "12px" }}>{v?.phone ?? "-"}</Typography>
            <Typography sx={{ fontSize: "12px" }}>{v?.email ?? "-"}</Typography>
          </Box>
        ),
        saleName: <Typography align="center">{v?.sales?.fullName ?? "-"}</Typography>,
        debt: (
          <Box sx={{ textAlign: "center" }}>
            <Typography>{!!v?.debtLimit ? convertIntMoney(v?.debtLimit) : 0}đ</Typography>
            <Typography sx={{ fontSize: "12px", fontStyle: "italic" }}>({!!v?.debtLimit ? convertCurrency(v?.debtLimit) : 0} đồng)</Typography>
          </Box>
        ),
        totalDebt: (
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                fontWeight: v?.debtLimit && v?.totalDebt > v.debtLimit * 0.9 ? "bold" : "normal",
                color: v?.debtLimit && v?.totalDebt > v.debtLimit * 0.9 ? "error.main" : "inherit",
              }}
            >
              {!!v?.totalDebt ? convertIntMoney(v.totalDebt) : 0}đ
            </Typography>

            <Typography
              sx={{
                fontSize: "12px",
                fontStyle: "italic",
                fontWeight: v?.debtLimit && v?.totalDebt > v.debtLimit * 0.9 ? "bold" : "normal",
                color: v?.debtLimit && v?.totalDebt > v.debtLimit * 0.9 ? "error.main" : "inherit",
              }}
            >
              ({!!v?.totalDebt ? convertCurrency(v.totalDebt) : 0} đồng)
            </Typography>
          </Box>
        ),
      }))
    : [];

  const sortData = ["checkbox", "no", "name", "info", "saleName", "debt", "totalDebt"];
  return {
    header,
    bodyList,
    sortData,
    total: customers?.count,
    mutate,
    selectedIds,
    setSelectedIds,
  };
}
